import * as React from "react";
import { useParams } from "react-router";

import { useAuth, useNavigation } from "@/providers";
import { Page } from "@/shared/components";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import { OrgInviteRecord } from "@/shared/domain/invites/InviteRecord";

import { InviteErrorFallback } from "./InviteErrorFallback";
import { InviteLoadingFallback } from "./InviteLoadingFallback";
import { InviteResponseForm } from "./InviteResponseForm";

/**
 * The top level route for a user to accept or decline an invite to an organization.
 */
export function Invite() {
  const { setCurrentOrganization, availableOrganizations } = useAuth();
  const { goto } = useNavigation();

  const { inviteId } = useParams();

  const onAccepted = (invite: OrgInviteRecord) => {
    setCurrentOrganization(invite.org);
    goto.home();
  };

  const onDeclined = () => goto.home();

  /**
   * If a referenced invite is missing, but they're already in orgs, we'll send them to the org picker.
   *
   * The most common scenario where this happens is when an invite is sent for an org with an "automatically add
   * users from this domain" setting, and the user will join the org through that rule before they get a chance
   * to accept the invite. This automatic step will delete the invite.
   *
   * For other cases where an invite doesn't exist for a user who has orgs to fall back on, redirecting to the org
   * picker is a reasonable fallback. For cases where a user doesn't have any orgs, we can keep them on the page
   * which says they don't have any invites, and direct them towards support.
   */
  const onMissing = React.useCallback(() => {
    if (availableOrganizations && availableOrganizations.length > 0) {
      goto.signIn({ chooseOrg: true });
    }
  }, [availableOrganizations, goto]);

  return (
    <Page title={"Roboto - Invite"}>
      <ErrorBoundary
        fallback={(props) => {
          return (
            <InviteErrorFallback error={props.error} onMissing={onMissing} />
          );
        }}
      >
        <React.Suspense fallback={<InviteLoadingFallback />}>
          <InviteResponseForm
            inviteId={inviteId}
            onAccepted={onAccepted}
            onDeclined={onDeclined}
          />
        </React.Suspense>
      </ErrorBoundary>
    </Page>
  );
}
